import { useQuery } from '@tanstack/react-query'
import { BookOpen, LayoutGrid, List } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { FilterBar, SearchInput } from '@/components/tables/FilterBar'
import { Pagination } from '@/components/tables/Pagination'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { useListParams } from '@/hooks/useListParams'
import { cn } from '@/utils/cn'
import { booksQuery, categoriesQuery, languagesQuery } from '../api'
import { formatIsbn } from '../isbn'
import type { Book } from '../types'
import { AvailabilityBadge } from './Badges'
import { BookCover } from './BookCover'

const FILTERS = ['categoryId', 'availability', 'status', 'language', 'view'] as const

interface CatalogBrowserProps {
  /** Where a book links to (admin detail page); omit for a non-navigating read-only catalogue. */
  detailHref?: (book: Book) => string
  /** Show the active/withdrawn filter (managers only). */
  showStatusFilter?: boolean
}

/** Searchable catalogue with a responsive cover grid and a compact list view. */
export function CatalogBrowser({ detailHref, showStatusFilter = false }: CatalogBrowserProps) {
  const { t } = useTranslation('library')
  const navigate = useNavigate()
  const { state, query, update, toggleSort, setPage } = useListParams(FILTERS, { sort: 'title', perPage: 24 })
  const { view, ...apiQuery } = query as typeof query & { view?: string }
  const list = useQuery(booksQuery(apiQuery))
  const categories = useQuery(categoriesQuery())
  const languages = useQuery(languagesQuery())
  const mode = view === 'list' ? 'list' : 'grid'
  const hasFilters = Boolean(
    state.search ||
    state.filters.categoryId ||
    state.filters.availability ||
    state.filters.language ||
    state.filters.status,
  )

  const columns: Column<Book>[] = [
    {
      key: 'title',
      header: t('Title'),
      sortKey: 'title',
      cell: (b) => (
        <div className="flex items-center gap-3">
          <BookCover title={b.title} src={b.coverUrl} size="xs" />
          <div className="min-w-0">
            {detailHref ? (
              <Link
                to={detailHref(b)}
                onClick={(e) => e.stopPropagation()}
                className="block truncate font-medium text-ink hover:text-brand-700"
              >
                {b.title}
              </Link>
            ) : (
              <span className="block truncate font-medium text-ink">{b.title}</span>
            )}
            <span className="block truncate text-xs text-muted">{b.authors}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: t('Category'),
      sortKey: 'category',
      hideBelow: 'lg',
      cell: (b) => b.category?.name ?? '—',
    },
    {
      key: 'isbn',
      header: t('ISBN'),
      hideBelow: 'xl',
      cell: (b) => <span className="font-mono text-xs">{formatIsbn(b.isbn)}</span>,
    },
    { key: 'shelf', header: t('Shelf'), hideBelow: 'xl', cell: (b) => b.shelfLocation ?? '—' },
    {
      key: 'year',
      header: t('Year'),
      sortKey: 'publishedYear',
      hideBelow: 'lg',
      cell: (b) => b.publishedYear ?? '—',
    },
    {
      key: 'availability',
      header: t('Availability'),
      sortKey: 'availableCopies',
      cell: (b) => <AvailabilityBadge book={b} />,
    },
  ]

  const empty = (
    <EmptyState
      icon={<BookOpen />}
      title={hasFilters ? t('No books match your search') : t('The catalogue is empty')}
      description={
        hasFilters
          ? t('Try another title, author or ISBN, or clear the filters.')
          : t('Books added to the library will appear here.')
      }
      compact
    />
  )

  return (
    <Card>
      <FilterBar
        actions={
          <>
            {hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  update({
                    search: undefined,
                    categoryId: undefined,
                    availability: undefined,
                    language: undefined,
                    status: undefined,
                  })
                }
              >
                {t('Clear')}
              </Button>
            ) : null}
            <div
              role="group"
              aria-label={t('Layout')}
              className="inline-flex rounded-control border border-line p-0.5"
            >
              {(['grid', 'list'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={mode === m}
                  aria-label={m === 'grid' ? t('Grid view') : t('List view')}
                  title={m === 'grid' ? t('Grid view') : t('List view')}
                  onClick={() => update({ view: m === 'grid' ? undefined : 'list' }, false)}
                  className={cn(
                    'grid size-8 place-items-center rounded-xs text-muted transition-colors hover:text-ink [&_svg]:size-4',
                    mode === m && 'bg-brand-50 text-brand-700',
                  )}
                >
                  {m === 'grid' ? <LayoutGrid /> : <List />}
                </button>
              ))}
            </div>
          </>
        }
      >
        <SearchInput
          value={state.search}
          onChange={(v) => update({ search: v })}
          placeholder={t('Title, author, ISBN or category')}
          label={t('Search the catalogue')}
        />
        <Select
          aria-label={t('Filter by category')}
          className="sm:w-auto sm:min-w-44"
          placeholder={t('All categories')}
          value={state.filters.categoryId ?? ''}
          onChange={(e) => update({ categoryId: e.target.value })}
          options={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />
        <Select
          aria-label={t('Filter by availability')}
          className="sm:w-auto sm:min-w-40"
          placeholder={t('Any availability')}
          value={state.filters.availability ?? ''}
          onChange={(e) => update({ availability: e.target.value })}
          options={[
            { value: 'available', label: t('Available now') },
            { value: 'unavailable', label: t('All on loan') },
          ]}
        />
        {(languages.data?.length ?? 0) > 1 ? (
          <Select
            aria-label={t('Filter by language')}
            className="sm:w-auto sm:min-w-36"
            placeholder={t('Any language')}
            value={state.filters.language ?? ''}
            onChange={(e) => update({ language: e.target.value })}
            options={(languages.data ?? []).map((l) => ({ value: l, label: l }))}
          />
        ) : null}
        {showStatusFilter ? (
          <Select
            aria-label={t('Filter by status')}
            className="sm:w-auto sm:min-w-36"
            value={state.filters.status ?? 'active'}
            onChange={(e) => update({ status: e.target.value === 'active' ? undefined : e.target.value })}
            options={[
              { value: 'active', label: t('In circulation') },
              { value: 'inactive', label: t('Withdrawn') },
              { value: 'all', label: t('All books') },
            ]}
          />
        ) : null}
      </FilterBar>

      {mode === 'list' ? (
        <DataTable
          caption={t('Library catalogue')}
          columns={columns}
          rows={list.data?.data}
          getRowId={(b) => b.id}
          loading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          sort={{ key: state.sort, direction: state.direction }}
          onSortChange={toggleSort}
          onRowClick={detailHref ? (b) => void navigate(detailHref(b)) : undefined}
          mobileCard={(b) => (
            <div className="flex items-center gap-3">
              <BookCover title={b.title} src={b.coverUrl} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{b.title}</p>
                <p className="truncate text-xs text-muted">{b.authors}</p>
                <div className="mt-1.5">
                  <AvailabilityBadge book={b} />
                </div>
              </div>
            </div>
          )}
          empty={empty}
        />
      ) : list.error ? (
        <ErrorState onRetry={() => void list.refetch()} compact />
      ) : (
        <div className="p-4 sm:p-5">
          {list.isPending ? (
            <ul
              className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 2xl:grid-cols-6"
              aria-label={t('Loading books')}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <li key={i} className="space-y-2">
                  <Skeleton className="aspect-[2/3] w-full rounded-control" />
                  <Skeleton className="h-3.5 w-4/5" />
                  <Skeleton className="h-3 w-1/2" />
                </li>
              ))}
            </ul>
          ) : list.data.data.length === 0 ? (
            empty
          ) : (
            <ul
              className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 2xl:grid-cols-6"
              aria-label={t('Library catalogue')}
            >
              {list.data.data.map((b) => (
                <li key={b.id}>
                  <BookCard book={b} href={detailHref?.(b)} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <Pagination
        meta={list.data?.meta}
        onPageChange={setPage}
        onPerPageChange={(n) => update({ perPage: n })}
      />
    </Card>
  )
}

function BookCard({ book, href }: { book: Book; href?: string }) {
  const body = (
    <>
      <BookCover
        title={book.title}
        authors={book.authors}
        src={book.coverUrl}
        className={cn(
          'transition-transform duration-200',
          href && 'group-hover:-translate-y-1 group-hover:shadow-raised',
        )}
      />
      <div className="mt-2.5 min-w-0">
        <p className="line-clamp-2 text-sm leading-snug font-medium text-ink group-hover:text-brand-700">
          {book.title}
        </p>
        <p className="mt-0.5 truncate text-xs text-muted">{book.authors}</p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <AvailabilityBadge book={book} />
          {book.category ? <span className="truncate text-2xs text-subtle">{book.category.name}</span> : null}
        </div>
      </div>
    </>
  )
  return href ? (
    <Link to={href} className="group block rounded-control focus-visible:outline-offset-4">
      {body}
    </Link>
  ) : (
    <div className="group">{body}</div>
  )
}
